import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isBefore, startOfDay, startOfYear, endOfYear, addYears, subYears, parseISO } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { getToken, clearSession } from "@/lib/auth";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Lock, ArrowLeft, Eraser, Wand2, Sparkles, Moon, Tag, ChevronDown, X, UserPlus } from "lucide-react";
import UnassignedWalkinsBanner from "@/components/admin/UnassignedWalkinsBanner";
import BookingPill from "@/components/admin/BookingPill";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";

const DAYS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Booking-extranet style collapsible section
function BookingSection({ title, subtitle, isOpen, onToggle, children, testid }) {
  return (
    <div className="border-b border-[#E8E2D9] last:border-b-0" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full py-3 flex items-start justify-between text-left hover:bg-[#FDFBF7] -mx-5 px-5 transition"
      >
        <div>
          <div className="font-semibold text-[#1E3A5F] text-sm">{title}</div>
          {subtitle && <div className="text-[11px] text-[#635F59] mt-0.5">{subtitle}</div>}
        </div>
        <ChevronDown className={`w-4 h-4 text-[#635F59] mt-1 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && <div className="pb-4">{children}</div>}
    </div>
  );
}

export default function AdminCalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  // View mode: "monthly" (existing detail view) | "yearly" (12 mini-calendars overview)
  const [viewMode, setViewMode] = useState("monthly");
  const [yearAnchor, setYearAnchor] = useState(startOfYear(new Date()));
  const [selectedDates, setSelectedDates] = useState([]); // array of YYYY-MM-DD
  const [drawerOpen, setDrawerOpen] = useState(false);
  // When user edits "Da/A" fields manually inside the drawer.
  // Initialised from selectedDates when the drawer opens; on change, replaces selectedDates.
  const [manualFromDate, setManualFromDate] = useState("");
  const [manualToDate, setManualToDate] = useState("");
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkBlocked, setBulkBlocked] = useState(false);
  const [bulkMinStay, setBulkMinStay] = useState("");
  // Promo / last-minute fields (applied to selected dates)
  const [bulkPromoLabel, setBulkPromoLabel] = useState("");
  const [bulkPromoType, setBulkPromoType] = useState("");
  const [bulkPromoPct, setBulkPromoPct] = useState("");
  // Booking-style: days of week filter (default: all selected)
  const [bulkDow, setBulkDow] = useState([0, 1, 2, 3, 4, 5, 6]); // 0=Mon
  // Inventory: units available
  const [bulkUnits, setBulkUnits] = useState("");
  // Status: open/close
  const [bulkStatus, setBulkStatus] = useState(""); // "open" | "close" | ""
  // Advanced restrictions
  const [bulkMaxStay, setBulkMaxStay] = useState("");
  const [bulkCTA, setBulkCTA] = useState(""); // "" | "yes" | "no" — closed to arrival
  const [bulkCTD, setBulkCTD] = useState(""); // closed to departure
  const [bulkMinAdvance, setBulkMinAdvance] = useState("");
  // Sezioni a fisarmonica
  const [openSection, setOpenSection] = useState("units"); // first open by default
  // Seasonal preset dialog state
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetYear, setPresetYear] = useState(String(new Date().getFullYear()));
  const [presetHigh, setPresetHigh] = useState("");
  const [presetMedium, setPresetMedium] = useState("");
  const [presetLow, setPresetLow] = useState("");
  const [presetWeekendPct, setPresetWeekendPct] = useState("20");
  const [presetWeekendMinStay, setPresetWeekendMinStay] = useState("2");
  const [presetSubmitting, setPresetSubmitting] = useState(false);
  // Rate plans for "applica al piano X"
  const [ratePlans, setRatePlans] = useState([]);
  const [bulkRatePlanId, setBulkRatePlanId] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    api.get("/rooms").then((r) => {
      setRooms(r.data);
      if (r.data.length > 0) setSelectedRoomId(r.data[0].id);
    }).catch(() => toast.error("Errore caricamento camere"));
    api.get("/admin/rate-plans").then((r) => {
      setRatePlans((r.data?.rate_plans || []).filter((p) => p.active !== false));
    }).catch(() => {
      // non-blocking: rate plans are optional
    });
  }, [navigate]);

  const fetchCalendar = useCallback(async () => {
    if (!selectedRoomId) return;
    setLoading(true);
    // Range depends on view mode: full year (annual overview) vs single month (detail)
    const from = viewMode === "yearly"
      ? format(startOfYear(yearAnchor), "yyyy-MM-dd")
      : format(startOfMonth(month), "yyyy-MM-dd");
    const to = viewMode === "yearly"
      ? format(endOfYear(yearAnchor), "yyyy-MM-dd")
      : format(endOfMonth(month), "yyyy-MM-dd");
    try {
      const res = await api.get(`/admin/calendar/${selectedRoomId}`, { params: { from_date: from, to_date: to } });
      setDays(res.data.days);
    } catch (err) {
      if (err.response?.status === 401) {
        clearSession();
        navigate("/admin/login");
      } else {
        toast.error("Errore caricamento calendario");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedRoomId, month, viewMode, yearAnchor, navigate]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Handle deep-links from Panoramica disponibilità:
  //   /admin/calendar?action=presets  → open Seasonal Preset dialog
  //   /admin/calendar?action=bulk     → highlight "select dates then Modifica"
  useEffect(() => {
    const action = searchParams.get("action");
    if (!action) return;
    if (action === "presets") {
      setPresetOpen(true);
      toast.info("Seleziona un preset stagionale da applicare");
    } else if (action === "bulk") {
      toast.info("Seleziona le date sul calendario, poi premi 'Modifica in blocco'");
    }
    // Clean up the query param so refreshing the page doesn't re-open
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthDays = useMemo(() => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    return eachDayOfInterval({ start, end });
  }, [month]);

  // Map for quick lookup
  const dayMap = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d.date] = d; });
    return m;
  }, [days]);

  const selectedRoom = rooms.find((r) => r.id === selectedRoomId);
  const today = startOfDay(new Date());

  const toggleDate = (d) => {
    const key = format(d, "yyyy-MM-dd");
    setSelectedDates((prev) => prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]);
  };

  // Drag-to-select state. When user holds mouse and drags across cells,
  // every cell crossed is added to the selection (does not deselect).
  const [dragMode, setDragMode] = useState(null); // null | "add" | "remove"
  const [dragSeen, setDragSeen] = useState(() => new Set());

  const onCellMouseDown = (d) => {
    const key = format(d, "yyyy-MM-dd");
    const alreadySelected = selectedDates.includes(key);
    const mode = alreadySelected ? "remove" : "add";
    setDragMode(mode);
    setDragSeen(new Set([key]));
    setSelectedDates((prev) => mode === "add"
      ? (prev.includes(key) ? prev : [...prev, key])
      : prev.filter((x) => x !== key));
  };

  const onCellMouseEnter = (d) => {
    if (!dragMode) return;
    const key = format(d, "yyyy-MM-dd");
    if (dragSeen.has(key)) return;
    setDragSeen((s) => new Set(s).add(key));
    setSelectedDates((prev) => dragMode === "add"
      ? (prev.includes(key) ? prev : [...prev, key])
      : prev.filter((x) => x !== key));
  };

  // End drag on mouse up anywhere
  useEffect(() => {
    if (!dragMode) return;
    const stop = () => { setDragMode(null); setDragSeen(new Set()); };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, [dragMode]);

  // Touchscreen drag-to-select: while dragging, watch touchmove on the document
  // and resolve the cell under the finger via elementFromPoint. Each cell has
  // a `data-testid="day-YYYY-MM-DD"` so we recover the date from there.
  useEffect(() => {
    if (!dragMode) return;
    const onTouchMove = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const cell = el?.closest?.('[data-testid^="day-"]');
      if (!cell) return;
      const key = cell.getAttribute("data-testid").replace("day-", "");
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
      if (dragSeen.has(key)) return;
      let d;
      try { d = parseISO(key); } catch { return; }
      if (dragSeen.has(key)) return;
      setDragSeen((s) => new Set(s).add(key));
      setSelectedDates((prev) => dragMode === "add"
        ? (prev.includes(key) ? prev : [...prev, key])
        : prev.filter((x) => x !== key));
      // Prevent the page from scrolling under the finger while selecting
      e.preventDefault();
    };
    const stop = () => { setDragMode(null); setDragSeen(new Set()); };
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", stop);
    document.addEventListener("touchcancel", stop);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", stop);
      document.removeEventListener("touchcancel", stop);
    };
  }, [dragMode, dragSeen, today]);

  const clearSelection = () => setSelectedDates([]);

  const openBulkEditor = () => {
    // Initialise manual From/To from current selection (if any).
    const sorted = [...selectedDates].sort();
    setManualFromDate(sorted[0] || "");
    setManualToDate(sorted[sorted.length - 1] || "");
    setBulkPrice("");
    setBulkBlocked(false);
    setBulkMinStay("");
    setBulkPromoLabel("");
    setBulkPromoType("");
    setBulkPromoPct("");
    setBulkDow([0, 1, 2, 3, 4, 5, 6]);
    setBulkUnits("");
    setBulkStatus("");
    setBulkMaxStay("");
    setBulkCTA("");
    setBulkCTD("");
    setBulkMinAdvance("");
    setBulkRatePlanId("");
    setOpenSection("units");
    setDrawerOpen(true);
  };

  // When user manually edits From/To, recompute selectedDates from the range.
  // If end < start the dates are clearly inverted — auto-swap them so the
  // user sees the expected behaviour instead of a silent failure.
  const applyManualRange = (from, to) => {
    if (!from || !to) return;
    try {
      let start = new Date(from + "T00:00:00");
      let end = new Date(to + "T00:00:00");
      if (end < start) {
        // Auto-swap and reflect the swap in the bound state so the inputs
        // visually reorder themselves (avoid stale "31/05" → "01/07" labels)
        [start, end] = [end, start];
        const newFrom = format(start, "yyyy-MM-dd");
        const newTo = format(end, "yyyy-MM-dd");
        setManualFromDate(newFrom);
        setManualToDate(newTo);
        toast.info(`📅 Date invertite — riordinate automaticamente: ${newFrom} → ${newTo}`);
      }
      const arr = [];
      const cur = new Date(start);
      while (cur <= end) {
        arr.push(format(cur, "yyyy-MM-dd"));
        cur.setDate(cur.getDate() + 1);
      }
      setSelectedDates(arr);
    } catch (e) { /* ignore */ }
  };

  const toggleDow = (n) => {
    setBulkDow((arr) => arr.includes(n) ? arr.filter((x) => x !== n) : [...arr, n].sort());
  };

  const applyBulk = async () => {
    // iter241 — Fix silenzioso "Applica modifiche" senza effetto. Prima
    // se `selectedDates` era vuoto la funzione usciva subito senza toast
    // → l'utente vedeva le due date "Dal/Al" ma non aveva mai cliccato
    // le celle del calendario e non capiva perché non salvasse.
    // Ora se ha impostato manualmente il range Dal/Al lo usiamo per
    // popolare `selectedDates` al volo; se comunque manca tutto,
    // mostriamo un toast esplicativo.
    let effectiveDates = selectedDates;
    if (effectiveDates.length === 0 && manualFromDate && manualToDate) {
      try {
        const start = new Date(manualFromDate + "T00:00:00");
        const end = new Date(manualToDate + "T00:00:00");
        const arr = [];
        const cur = new Date(Math.min(start, end));
        const stop = new Date(Math.max(start, end));
        while (cur <= stop) {
          arr.push(format(cur, "yyyy-MM-dd"));
          cur.setDate(cur.getDate() + 1);
        }
        effectiveDates = arr;
        setSelectedDates(arr);
      } catch (e) { /* ignore */ }
    }
    if (effectiveDates.length === 0) {
      toast.error("Nessuna data selezionata. Imposta Dal / Al oppure clicca le celle del calendario.");
      return;
    }
    const sorted = [...effectiveDates].sort();
    const from_date = sorted[0];
    const to_date = sorted[sorted.length - 1];

    // Use the user-chosen days_of_week if not all are selected
    let days_of_week = null;
    if (bulkDow.length > 0 && bulkDow.length < 7) {
      days_of_week = bulkDow;
    } else if (sorted.length < (Math.round((new Date(to_date) - new Date(from_date)) / 86400000) + 1)) {
      days_of_week = [...new Set(sorted.map((s) => {
        const d = new Date(s + "T00:00:00");
        return (d.getDay() + 6) % 7;
      }))];
    }

    const body = { from_date, to_date };
    if (bulkPrice !== "") body.price = parseFloat(bulkPrice);
    if (bulkStatus === "close" || bulkBlocked) body.blocked = true;
    else if (bulkStatus === "open") body.blocked = false;
    if (bulkMinStay !== "") body.min_stay = parseInt(bulkMinStay) || 0;
    if (bulkMaxStay !== "") body.max_stay = parseInt(bulkMaxStay) || 0;
    if (bulkMinAdvance !== "") body.min_advance_days = parseInt(bulkMinAdvance) || 0;
    if (bulkUnits !== "") body.units_available = parseInt(bulkUnits) || 0;
    if (bulkCTA === "yes") body.closed_to_arrival = true;
    else if (bulkCTA === "no") body.closed_to_arrival = false;
    if (bulkCTD === "yes") body.closed_to_departure = true;
    else if (bulkCTD === "no") body.closed_to_departure = false;
    if (bulkRatePlanId) body.rate_plan_id = bulkRatePlanId;
    // Promo
    if (bulkPromoLabel !== "") {
      const cleared = bulkPromoLabel.trim() === "—" || bulkPromoLabel.trim() === "-";
      body.promo_label = cleared ? "" : bulkPromoLabel;
      if (cleared) {
        body.promo_type = "";
        body.promo_discount_pct = 0;
      }
    }
    if (bulkPromoType !== "" && body.promo_label !== "") body.promo_type = bulkPromoType;
    if (bulkPromoPct !== "" && body.promo_label !== "") body.promo_discount_pct = parseFloat(bulkPromoPct) || 0;
    if (days_of_week) body.days_of_week = days_of_week;

    try {
      const res = await api.put(`/admin/calendar/${selectedRoomId}/bulk`, body);
      const updated = res.data?.updated ?? effectiveDates.length;
      // Build a precise summary so the host can verify what was actually saved
      const parts = [];
      if (body.units_available !== undefined) parts.push(`Unità in vendita → ${body.units_available}`);
      if (body.price !== undefined) parts.push(`Prezzo → €${body.price}`);
      if (body.min_stay !== undefined) parts.push(`Min-stay → ${body.min_stay}`);
      if (body.max_stay !== undefined) parts.push(`Max-stay → ${body.max_stay}`);
      if (body.blocked === true) parts.push("Status → CHIUSO");
      else if (body.blocked === false) parts.push("Status → APERTO");
      const summary = parts.length ? ` · ${parts.join(" · ")}` : "";
      if (body.blocked === false) {
        toast.success(`✓ ${updated} date aggiornate${summary}. I lucchetti OTA restano visibili: ricordati di riaprire anche su Booking/Airbnb.`, { duration: 9000 });
      } else {
        toast.success(`✓ ${updated} date aggiornate${summary}`, { duration: 6000 });
      }
      setSelectedDates([]);
      setDrawerOpen(false);
      fetchCalendar();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento"));
    }
  };

  const applyPreset = async () => {
    if (!presetHigh) {
      toast.error("Inserisci almeno il prezzo Alta stagione");
      return;
    }
    setPresetSubmitting(true);
    const body = {
      year: parseInt(presetYear),
      high_price: parseFloat(presetHigh),
    };
    if (presetMedium) body.medium_price = parseFloat(presetMedium);
    if (presetLow) body.low_price = parseFloat(presetLow);
    if (presetWeekendPct) body.weekend_supplement_pct = parseFloat(presetWeekendPct);
    if (presetWeekendMinStay) body.weekend_min_stay = parseInt(presetWeekendMinStay);
    try {
      const res = await api.post(`/admin/calendar/${selectedRoomId}/preset`, body);
      toast.success(`✓ Preset applicato: ${res.data.written} date scritte per il ${res.data.year}`);
      setPresetOpen(false);
      fetchCalendar();
    } catch (err) {
      toast.error(errMsg(err, "Errore applicazione preset"));
    } finally {
      setPresetSubmitting(false);
    }
  };

  const resetSelected = async () => {
    if (selectedDates.length === 0) return;
    const sorted = [...selectedDates].sort();
    try {
      await api.delete(`/admin/calendar/${selectedRoomId}/bulk`, {
        params: { from_date: sorted[0], to_date: sorted[sorted.length - 1] },
      });
      toast.success("✓ Date riportate al prezzo base");
      setSelectedDates([]);
      fetchCalendar();
    } catch {
      toast.error("Errore reset");
    }
  };

  // Double-click on a calendar cell = toggle blocked (close/open) for that single date.
  // Quick shortcut so the user can mark a day as closed without opening the bulk editor.
  const toggleDayBlock = useCallback(async (date, currentlyBlocked) => {
    const iso = format(date, "yyyy-MM-dd");
    try {
      await api.put(`/admin/calendar/${selectedRoomId}/bulk`, {
        from_date: iso,
        to_date: iso,
        blocked: !currentlyBlocked,
      });
      toast.success(currentlyBlocked ? `✓ ${iso} riaperta` : `🔴 ${iso} chiusa`);
      fetchCalendar();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento"));
    }
  }, [selectedRoomId, fetchCalendar]);

  // Bulk toggle a date range (drag-to-select)
  const bulkToggleRange = useCallback(async (fromDate, toDate, action) => {
    if (!fromDate || !toDate || !selectedRoomId) return;
    const fromIso = format(fromDate < toDate ? fromDate : toDate, "yyyy-MM-dd");
    const toIso = format(fromDate < toDate ? toDate : fromDate, "yyyy-MM-dd");
    try {
      const res = await api.put(`/admin/calendar/${selectedRoomId}/bulk`, {
        from_date: fromIso,
        to_date: toIso,
        blocked: action === "close",
      });
      const updated = res?.data?.updated ?? 0;
      if (action === "close") {
        toast.success(`🔴 Chiusi ${updated} giorni (${fromIso} → ${toIso})`);
      } else {
        toast.success(`✓ Riaperti ${updated} giorni (${fromIso} → ${toIso}). I lucchetti OTA restano visibili: ricordati di riaprire anche su Booking/Airbnb se vuoi rendere disponibile la camera lato OTA.`, { duration: 9000 });
      }
      fetchCalendar();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento"));
    }
  }, [selectedRoomId, fetchCalendar]);

  // --- Drag-to-select state for the yearly view ---
  const [dragRange, setDragRange] = useState(null); // {start: Date, end: Date}
  const dragActiveRef = useRef(false);
  const startDrag = useCallback((date) => {
    dragActiveRef.current = true;
    setDragRange({ start: date, end: date });
  }, []);
  const extendDrag = useCallback((date) => {
    if (!dragActiveRef.current) return;
    setDragRange((p) => (p ? { ...p, end: date } : { start: date, end: date }));
  }, []);
  const endDrag = useCallback(() => {
    dragActiveRef.current = false;
    // Keep dragRange visible until user clicks a bulk action or clears
  }, []);
  const clearDrag = useCallback(() => setDragRange(null), []);

  // Touchscreen drag-to-select for the YEARLY mini-month view. Each cell has
  // a `data-day="YYYY-MM-DD"` attribute that we read with elementFromPoint.
  useEffect(() => {
    if (viewMode !== "yearly") return;
    const onTouchMove = (e) => {
      if (!dragActiveRef.current) return;
      const t = e.touches?.[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const cell = el?.closest?.("[data-day]");
      if (!cell) return;
      const key = cell.getAttribute("data-day");
      if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
      let d;
      try { d = parseISO(key); } catch { return; }
      extendDrag(d);
      e.preventDefault();
    };
    const onTouchEnd = () => { endDrag(); };
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [viewMode, today, extendDrag, endDrag]);

  // Compute selected ISO date strings for the range
  const selectedDateSet = useMemo(() => {
    if (!dragRange) return new Set();
    const a = dragRange.start < dragRange.end ? dragRange.start : dragRange.end;
    const b = dragRange.start < dragRange.end ? dragRange.end : dragRange.start;
    return new Set(eachDayOfInterval({ start: a, end: b }).map((d) => format(d, "yyyy-MM-dd")));
  }, [dragRange]);

  const startWeekday = (startOfMonth(month).getDay() + 6) % 7; // Mon=0
  const padding = Array.from({ length: startWeekday });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#1E3A5F] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/admin/dashboard")} variant="outline" size="sm" data-testid="back-dashboard" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />Dashboard
            </Button>
            <Button onClick={() => navigate("/admin/channel-manager")} variant="outline" size="sm" data-testid="goto-channel-manager" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
              <CalIcon className="w-4 h-4 mr-2" />Panoramica disponibilità
            </Button>
            <div>
              <h1 className="font-display text-2xl">Disponibilità Camere</h1>
              <p className="text-xs text-white/60">Stato prenotazioni · Tariffe · Promo · Blocchi</p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="outline"
            size="sm"
            data-testid="close-calendar-page"
            className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white shrink-0"
            title="Chiudi e torna alla Dashboard"
          >
            <X className="w-4 h-4 mr-1.5" />Chiudi
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        {/* Walk-in dall'App Guida senza camera assegnata */}
        <div className="mb-5">
          <UnassignedWalkinsBanner />
        </div>
        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-between mb-6">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="min-w-[280px]">
              <Label className="text-xs text-[#635F59] mb-1.5">Camera</Label>
              <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
                <SelectTrigger data-testid="cal-room-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rooms.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`cal-room-${r.slug}`}>
                      {r.name?.it}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={() => viewMode === "yearly" ? setYearAnchor(subYears(yearAnchor, 1)) : setMonth(subMonths(month, 1))} data-testid="prev-month" className="h-11 bg-white">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="font-display text-2xl text-[#1E3A5F] capitalize px-3 min-w-[180px] text-center" data-testid="cal-month-label">
                {viewMode === "yearly" ? format(yearAnchor, "yyyy") : format(month, "MMMM yyyy", { locale: itLocale })}
              </div>
              <Button variant="outline" onClick={() => viewMode === "yearly" ? setYearAnchor(addYears(yearAnchor, 1)) : setMonth(addMonths(month, 1))} data-testid="next-month" className="h-11 bg-white">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {/* View mode toggle: Monthly (existing) | Yearly (12-month overview) */}
            <div className="flex items-end">
              <div className="inline-flex border border-[#E8E2D9] rounded-lg bg-white overflow-hidden h-11" data-testid="view-mode-toggle">
                <button
                  type="button"
                  onClick={() => setViewMode("monthly")}
                  data-testid="view-monthly"
                  className={`px-3 text-xs font-semibold transition ${viewMode === "monthly" ? "bg-[#1E3A5F] text-white" : "text-[#635F59] hover:bg-[#FDFBF7]"}`}
                >
                  Mensile
                </button>
                <button
                  type="button"
                  onClick={() => { setViewMode("yearly"); setYearAnchor(startOfYear(month)); }}
                  data-testid="view-yearly"
                  className={`px-3 text-xs font-semibold transition ${viewMode === "yearly" ? "bg-[#1E3A5F] text-white" : "text-[#635F59] hover:bg-[#FDFBF7]"}`}
                >
                  Annuale
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {selectedDates.length > 0 && (
              <span className="text-sm text-[#635F59] mr-2" data-testid="selected-count">{selectedDates.length} date selezionate</span>
            )}
            <Button variant="outline" onClick={clearSelection} disabled={selectedDates.length === 0} data-testid="clear-selection" className="h-11 bg-white">
              Pulisci
            </Button>
            <Button variant="outline" onClick={resetSelected} disabled={selectedDates.length === 0} data-testid="reset-selected" className="h-11 bg-white">
              <Eraser className="w-4 h-4 mr-1.5" />Reset
            </Button>
            <Button onClick={openBulkEditor} data-testid="bulk-edit" className="h-11 bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              <Wand2 className="w-4 h-4 mr-1.5" />Modifica{selectedDates.length > 0 ? ` (${selectedDates.length})` : ""}
            </Button>
            <Button onClick={() => setPresetOpen(true)} data-testid="open-preset" className="h-11 bg-[#1E3A5F] hover:bg-[#152A45] text-white">
              <Sparkles className="w-4 h-4 mr-1.5" />Preset stagionali
            </Button>
          </div>
        </div>

        {/* Legend — identica al PMS */}
        <div className="space-y-1 mb-3 px-1 pb-2 border-b border-[#E8E2D9]">
          <div className="flex flex-wrap gap-3 md:gap-5 text-xs text-[#635F59] items-center">
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 bg-emerald-500 border border-emerald-700" />
              Libero
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 bg-orange-500 border border-orange-700" />
              Prenotato
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 bg-rose-500 border border-rose-700 flex items-center justify-center text-white text-[8px] font-bold">✕</span>
              Sold-out / blocco manuale
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold rounded-md px-1 py-0.5 border-2 bg-pink-100 border-pink-400 text-pink-700">
                <Lock className="w-2 h-2 text-amber-500" strokeWidth={3} />A
              </span>
              Blocco Airbnb
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold rounded-md px-1 py-0.5 border-2 bg-sky-100 border-sky-400 text-sky-700">
                <Lock className="w-2 h-2 text-amber-500" strokeWidth={3} />B
              </span>
              Blocco Booking
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#635F59] items-center" data-testid="pill-format-legend">
            <span className="font-semibold text-[#1E3A5F]">Fonte:</span>
            <span className="flex items-center gap-1">
              <span className="text-orange-600 font-extrabold">W</span> walk-in
            </span>
            <span className="flex items-center gap-1">
              <span className="text-emerald-700 font-extrabold">S</span> sito diretta
            </span>
            <span className="flex items-center gap-1">
              <span className="text-sky-700 font-extrabold">B</span> Booking
            </span>
            <span className="flex items-center gap-1">
              <span className="text-pink-700 font-extrabold">A</span> Airbnb
            </span>
            <span className="text-[#AC5D45] italic">
              es <strong className="not-italic">n13 B Souite · 2p</strong>
            </span>
          </div>
        </div>

        {/* Grid — stile prenotazioni (celle min-h, border, no gap) */}
        {viewMode === "yearly" ? (
          <>
            {/* Drag-action toolbar (visible only while a range is selected) */}
            {dragRange && (
              <div className="sticky top-2 z-30 mx-auto max-w-2xl mb-4 bg-white border border-[#1E3A5F] rounded-xl shadow-lg p-3 flex items-center justify-between gap-3" data-testid="drag-action-toolbar">
                <div className="text-xs text-[#1E3A5F]">
                  <strong>Range selezionato:</strong> {selectedDateSet.size} giorni
                  <span className="text-[#635F59] ml-2 hidden sm:inline">
                    ({format(dragRange.start < dragRange.end ? dragRange.start : dragRange.end, "dd/MM/yyyy")} → {format(dragRange.start < dragRange.end ? dragRange.end : dragRange.start, "dd/MM/yyyy")})
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => { const r = dragRange; clearDrag(); bulkToggleRange(r.start, r.end, "close"); }} size="sm" className="bg-rose-600 hover:bg-rose-700 text-white text-xs h-8" data-testid="drag-close-range">🔴 Chiudi</Button>
                  <Button onClick={() => { const r = dragRange; clearDrag(); bulkToggleRange(r.start, r.end, "open"); }} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid="drag-open-range">✓ Riapri</Button>
                  <Button onClick={clearDrag} size="sm" variant="outline" className="text-xs h-8" data-testid="drag-cancel">Annulla</Button>
                </div>
              </div>
            )}
            <YearGrid
              yearAnchor={yearAnchor}
              dayMap={dayMap}
              today={today}
              onPickMonth={(m) => { setMonth(m); setViewMode("monthly"); }}
              onToggleDayBlock={toggleDayBlock}
              selectedDateSet={selectedDateSet}
              onStartDrag={startDrag}
              onExtendDrag={extendDrag}
              onEndDrag={endDrag}
            />
          </>
        ) : (
        <div className="bg-white border border-[#E8E2D9] rounded-2xl overflow-hidden" data-testid="calendar-grid">
          <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-[#635F59] bg-[#FDFBF7] border-b border-[#E8E2D9]">
            {DAYS_SHORT.map((d) => <div key={d} className="py-2 text-center font-semibold">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {padding.map((_, i) => <div key={`p-${i}`} className="bg-[#FDFBF7] border-b border-r border-[#E8E2D9] min-h-[100px]" />)}
            {monthDays.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const info = dayMap[key];
              const isPast = isBefore(d, today) && !isSameDay(d, today);
              const isSelected = selectedDates.includes(key);
              const isTd = isSameDay(d, today);
              const blocked = info?.blocked;
              const availability = info?.availability;
              const booking = info?.booking;
              const override = info?.has_override;
              const price = info?.price ?? selectedRoom?.price_per_night;

              const cellBg = isSelected
                ? "bg-[#1E3A5F]/10 ring-2 ring-[#1E3A5F] ring-inset z-10"
                : blocked
                ? "bg-rose-50"
                : availability === "booked"
                ? "bg-orange-50"
                : availability === "external"
                ? "bg-sky-50"
                : availability === "ota_block"
                ? "bg-rose-50/70"  // pastel rose so it's visually different from "free" green; A/B lock badges go in the corners
                : override
                ? "bg-teal-50/60"
                : "bg-emerald-50/40";

              return (
                <button
                  key={key}
                  type="button"
                  disabled={loading}
                  onClick={(e) => {
                    // Skip onClick if a drag just happened (mouse up after enter on >1 cell)
                    if (dragSeen.size > 1) { e.preventDefault(); return; }
                    toggleDate(d);
                  }}
                  onMouseDown={() => onCellMouseDown(d)}
                  onMouseEnter={() => onCellMouseEnter(d)}
                  onTouchStart={() => {
                    if (loading) return;
                    onCellMouseDown(d);
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (loading) return;
                    toggleDayBlock(d, !!blocked);
                  }}
                  data-testid={`day-${key}`}
                  title={blocked ? "Doppio click per RIAPRIRE · Trascina per selezione multipla" : "Doppio click per CHIUDERE · Trascina per selezione multipla"}
                  className={`relative min-h-[100px] border-b border-r border-[#E8E2D9] p-1.5 text-left transition group hover:bg-[#FDFBF7] disabled:cursor-not-allowed select-none ${cellBg} ${isPast ? "opacity-80" : ""}`}
                >
                  {/* Tiny OTA lock badges in the cell corners. Airbnb top-left,
                      Booking top-right. Shown whenever the date is effectively
                      closed on that OTA: either via an inbound OTA block, or
                      via a real booking (which we outbound-export to both
                      OTAs as CLOSED). Small round-ish style, consistent across
                      every cell — same look as in the Yearly Calendar. */}
                  {(() => {
                    const overlay = info?.ota_block_overlay || info?.booking?.ota_block_overlay || (availability === "ota_block" ? booking?.source : "");
                    const otaSrc = (overlay || "").toLowerCase();
                    const isRealOccupant = availability === "booked" || availability === "external" || (blocked && availability !== "ota_block");
                    const showAirbnb = isRealOccupant || otaSrc.includes("airbnb");
                    const showBooking = isRealOccupant || otaSrc.includes("booking");
                    if (!showAirbnb && !showBooking) return null;
                    return (
                      <>
                        {showAirbnb && (
                          <span
                            className="absolute top-0.5 left-0.5 inline-flex items-center justify-center gap-0.5 text-[8px] font-extrabold rounded-full px-1 py-[1px] border bg-pink-100 border-pink-500 text-pink-700 leading-none shadow-sm pointer-events-none z-10"
                            title={`🔒 Chiuso su Airbnb`}
                            data-testid={`ota-lock-airbnb-${key}`}
                          >
                            <Lock className="w-1.5 h-1.5 text-amber-500 shrink-0" />A
                          </span>
                        )}
                        {showBooking && (
                          <span
                            className="absolute top-0.5 right-0.5 inline-flex items-center justify-center gap-0.5 text-[8px] font-extrabold rounded-full px-1 py-[1px] border bg-sky-100 border-sky-500 text-sky-700 leading-none shadow-sm pointer-events-none z-10"
                            title={`🔒 Chiuso su Booking`}
                            data-testid={`ota-lock-booking-${key}`}
                          >
                            <Lock className="w-1.5 h-1.5 text-amber-500 shrink-0" />B
                          </span>
                        )}
                      </>
                    );
                  })()}
                  <div className="flex items-start justify-between">
                    <span className={`text-sm ${isTd ? "bg-[#AC5D45] text-white rounded-full w-6 h-6 inline-flex items-center justify-center font-bold" : "text-[#1E3A5F] font-semibold"}`}>
                      {format(d, "d")}
                    </span>
                    {/* Status pallino: colored dot with count (1 unit = single B&B room).
                        Green = available · Orange = booked (site) · Sky = external · Rose = closed */}
                    {!isPast && (() => {
                      const cnt = info?.units_available ?? 1;
                      let cls = "bg-emerald-500 text-white"; // free
                      let txt = String(cnt);
                      // PRIORITY: a real occupant always wins over manual block
                      if (availability === "booked") {
                        cls = booking?.source === "walk_in" ? "bg-amber-500 text-white" : "bg-orange-500 text-white";
                        txt = "1";
                      } else if (availability === "external") {
                        cls = "bg-sky-500 text-white"; txt = "1";
                      } else if (availability === "ota_block") {
                        cls = "bg-rose-300 text-rose-900"; txt = "🔒";
                      } else if (blocked) {
                        cls = "bg-rose-500 text-white"; txt = "✕";
                      }
                      return (
                        <span
                          className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold shrink-0 shadow-sm ${cls}`}
                          title={availability === "booked" ? (booking?.source === "walk_in" ? `Walk-in: ${booking?.guest}` : "Prenotato dal sito") : availability === "external" ? "Prenotato esterno (Booking/Airbnb)" : availability === "ota_block" ? "Blocco OTA — data chiusa sul canale Airbnb/Booking" : blocked ? "Chiuso" : `${cnt} unità disponibile/i`}
                          data-testid={`status-dot-${key}`}
                        >
                          {txt}
                        </span>
                      );
                    })()}
                    {info?.min_stay > 1 && !blocked && (
                      <span
                        className="text-[9px] px-1 rounded font-bold bg-[#1E3A5F] text-white"
                        title={`Min ${info.min_stay} notti`}
                        data-testid={`min-stay-${key}`}
                      >
                        {info.min_stay}n
                      </span>
                    )}
                  </div>

                  {/* Pillole compatte */}
                  <div className="mt-1 space-y-0.5">
                    {info?.promo_label && !blocked && (
                      <div
                        className="flex items-center gap-1 text-[9px] truncate rounded px-1 py-0.5 bg-pink-600 text-white font-bold"
                        title={info.promo_label}
                        data-testid={`promo-${key}`}
                      >
                        <Tag className="w-2 h-2 shrink-0" />
                        {info.promo_discount_pct ? `-${Math.round(info.promo_discount_pct)}%` : "PROMO"}
                      </div>
                    )}
                    {availability === "booked" ? (
                      <div
                        className={`truncate rounded px-1 py-0.5 border ${booking?.source === "walk_in" ? "bg-amber-100 border-amber-400" : "bg-orange-100 border-orange-400"}`}
                        data-testid={`booking-pill-${key}`}
                      >
                        <BookingPill booking={booking || {}} compact />
                      </div>
                    ) : availability === "external" ? (
                      <div
                        className="truncate rounded px-1 py-0.5 border bg-sky-100 border-sky-400"
                        data-testid={`external-pill-${key}`}
                      >
                        <BookingPill booking={booking || {}} compact />
                      </div>
                    ) : availability === "ota_block" ? (
                      // OTA block: badge already shown as a small corner lock.
                      // Show the room's base price so the host can still book
                      // a direct guest if they reopen the date.
                      <div className={`text-[11px] font-semibold ${override ? "text-teal-700" : "text-[#1E3A5F]"}`}>
                        €{price}
                      </div>
                    ) : blocked ? (
                      <div className="flex items-center gap-1 text-[10px] truncate rounded px-1 py-0.5 bg-rose-200 border border-rose-500 text-rose-900">
                        <Lock className="w-2.5 h-2.5 shrink-0" />Chiuso
                      </div>
                    ) : (
                      <div className={`text-[11px] font-semibold ${override ? "text-teal-700" : "text-[#1E3A5F]"}`}>
                        €{price}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        <p className="text-xs text-[#635F59] mt-4">
          {viewMode === "yearly"
            ? <>📅 Vista annuale: tutti i 12 mesi a colpo d'occhio. <strong>Click</strong> sul nome di un mese o su un giorno per aprire la vista mensile dettagliata. <strong>Doppio click su un giorno</strong> per chiuderlo o riaprirlo direttamente (1 unità di default — modifica più dettagli dalla vista mensile).</>
            : <>💡 Suggerimento: clicca per selezionare un giorno · <strong>Trascina con il mouse</strong> per selezionare più giorni in una volta · poi premi <strong>Modifica</strong> per applicare prezzo/blocco. <strong>Doppio click su un giorno</strong> per chiuderlo/riaprirlo subito.</>}
        </p>
      </main>

      {/* Bulk edit drawer — Booking Extranet style */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto p-0"
          onPointerDownOutside={(e) => {
            // Prevent the Sheet from closing when the user clicks inside a
            // Radix Select / Popover portal that is rendered at the document
            // root (outside the SheetContent DOM). Without this, the Sheet
            // closes the moment you click on a dropdown option.
            const target = e.target;
            if (target && target.closest && target.closest("[data-radix-popper-content-wrapper], [role=listbox], [role=option]")) {
              e.preventDefault();
            }
          }}
        >
          <div className="sticky top-0 bg-white border-b border-[#E8E2D9] p-5 z-10">
            <SheetHeader>
              <SheetTitle className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
                <CalIcon className="w-5 h-5" />Modifica in blocco
              </SheetTitle>
              <SheetDescription>
                {selectedDates.length} {selectedDates.length === 1 ? "data" : "date"} · {selectedRoom?.name?.it}
              </SheetDescription>
            </SheetHeader>

            {/* Date range + DOW selector (always visible at top) */}
            <div className="mt-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Da:</Label>
                  <DateInputIT value={manualFromDate || (selectedDates.length ? [...selectedDates].sort()[0] : "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setManualFromDate(v);
                      applyManualRange(v, manualToDate || (selectedDates.length ? [...selectedDates].sort().slice(-1)[0] : v));
                    }}
                    data-testid="bulk-from-date"
                    className="mt-1 h-9 text-xs font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">A (incluso):</Label>
                  <DateInputIT value={manualToDate || (selectedDates.length ? [...selectedDates].sort().slice(-1)[0] : "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setManualToDate(v);
                      applyManualRange(manualFromDate || (selectedDates.length ? [...selectedDates].sort()[0] : v), v);
                    }}
                    data-testid="bulk-to-date"
                    className="mt-1 h-9 text-xs font-mono"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">A quali giorni della settimana applicare le modifiche?</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {["lun", "mar", "mer", "gio", "ven", "sab", "dom"].map((d, i) => {
                    const selected = bulkDow.includes(i);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDow(i)}
                        data-testid={`bulk-dow-${d}`}
                        aria-pressed={selected}
                        className={`px-2.5 py-1 rounded border text-xs font-semibold transition cursor-pointer select-none ${
                          selected
                            ? "bg-[#AC5D45] text-white border-[#AC5D45] shadow-sm"
                            : "bg-white border-[#E8E2D9] text-[#635F59] hover:border-[#AC5D45] hover:text-[#AC5D45]"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#635F59] mt-1">
                  {bulkDow.length === 7 ? "Tutti i giorni della settimana selezionati" : `${bulkDow.length}/7 giorni selezionati`}
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-2">
            {/* RATE PLAN selector (always visible) */}
            {ratePlans.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-3">
                <Label className="text-xs flex items-center gap-1 mb-1.5 text-blue-900">
                  <Tag className="w-3.5 h-3.5" />Applica al piano tariffario
                </Label>
                <Select value={bulkRatePlanId || "all"} onValueChange={(v) => setBulkRatePlanId(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-10 bg-white" data-testid="bulk-rate-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i piani (Standard)</SelectItem>
                    {ratePlans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.discount_pct ? `(${p.discount_pct > 0 ? "+" : ""}${p.discount_pct}%)` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-blue-800/80 mt-1.5">
                  💡 Le modifiche di prezzo/restrizioni si applicheranno solo al piano selezionato. Lascia "Tutti i piani" per applicare a tutte le tariffe.
                </p>
              </div>
            )}

            {/* SECTION: Unità da vendere */}
            <BookingSection
              title="Unità da vendere"
              subtitle="Aggiorna il numero di unità in vendita per questa tipologia di alloggio"
              isOpen={openSection === "units"}
              onToggle={() => setOpenSection(openSection === "units" ? "" : "units")}
              testid="section-units"
            >
              <Input
                type="number"
                min="0"
                step="1"
                value={bulkUnits}
                onChange={(e) => setBulkUnits(e.target.value)}
                placeholder="Unità"
                data-testid="bulk-units"
                className="h-10"
              />
              <p className="text-[11px] text-[#635F59] mt-2">
                Lascia vuoto per non modificare. Inserisci 0 per chiudere completamente la disponibilità.
              </p>
            </BookingSection>

            {/* SECTION: Prezzi */}
            <BookingSection
              title="Prezzi"
              subtitle="Modifica il prezzo per questa unità"
              isOpen={openSection === "prices"}
              onToggle={() => setOpenSection(openSection === "prices" ? "" : "prices")}
              testid="section-prices"
            >
              <Label className="text-xs">Prezzo personalizzato</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  placeholder={`Base: €${selectedRoom?.price_per_night}`}
                  data-testid="bulk-price"
                  className="h-10"
                />
                <div className="h-10 px-3 flex items-center bg-[#FDFBF7] border border-[#E8E2D9] rounded text-sm">€</div>
              </div>
              <p className="text-[11px] text-[#635F59] mt-2">
                Lascia vuoto per non modificare il prezzo base della camera.
              </p>
            </BookingSection>

            {/* SECTION: Status (apri/chiudi) */}
            <BookingSection
              title="Status"
              subtitle="Apri o chiudi questo alloggio nelle date selezionate"
              isOpen={openSection === "status"}
              onToggle={() => setOpenSection(openSection === "status" ? "" : "status")}
              testid="section-status"
            >
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-status"
                    value="open"
                    checked={bulkStatus === "open"}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    data-testid="bulk-status-open"
                  />
                  <span className="text-sm">🟢 Apri alloggio</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-status"
                    value="close"
                    checked={bulkStatus === "close"}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    data-testid="bulk-status-close"
                  />
                  <span className="text-sm">🔴 Chiudi alloggio (blocca)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-status"
                    value=""
                    checked={bulkStatus === ""}
                    onChange={(e) => setBulkStatus(e.target.value)}
                  />
                  <span className="text-sm text-[#635F59]">— Non modificare</span>
                </label>
              </div>
            </BookingSection>

            {/* SECTION: Restrizioni soggiorno */}
            <BookingSection
              title="Restrizioni soggiorno"
              subtitle="Soggiorno minimo/massimo, anticipo, check-in/check-out"
              isOpen={openSection === "rest"}
              onToggle={() => setOpenSection(openSection === "rest" ? "" : "rest")}
              testid="section-restrictions"
            >
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Soggiorno min. (notti)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={bulkMinStay}
                      onChange={(e) => setBulkMinStay(e.target.value)}
                      placeholder="es. 2"
                      data-testid="bulk-min-stay"
                      className="mt-1.5 h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Soggiorno max. (notti)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={bulkMaxStay}
                      onChange={(e) => setBulkMaxStay(e.target.value)}
                      placeholder="0 = no limite"
                      data-testid="bulk-max-stay"
                      className="mt-1.5 h-10"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Anticipo minimo prenotazione (giorni)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={bulkMinAdvance}
                    onChange={(e) => setBulkMinAdvance(e.target.value)}
                    placeholder="0 = nessun anticipo"
                    data-testid="bulk-min-advance"
                    className="mt-1.5 h-10"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Chiuso all'arrivo</Label>
                    <Select value={bulkCTA || "none"} onValueChange={(v) => setBulkCTA(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="bulk-cta">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Non modificare</SelectItem>
                        <SelectItem value="yes">Sì (no check-in)</SelectItem>
                        <SelectItem value="no">No (check-in OK)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Chiuso alla partenza</Label>
                    <Select value={bulkCTD || "none"} onValueChange={(v) => setBulkCTD(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="bulk-ctd">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Non modificare</SelectItem>
                        <SelectItem value="yes">Sì (no check-out)</SelectItem>
                        <SelectItem value="no">No (check-out OK)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </BookingSection>

            {/* SECTION: Promo */}
            <BookingSection
              title="Promo · Last minute · Sconto"
              subtitle="Banner promozionali e sconti % sulle date selezionate"
              isOpen={openSection === "promo"}
              onToggle={() => setOpenSection(openSection === "promo" ? "" : "promo")}
              testid="section-promo"
            >
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Etichetta promo</Label>
                  <Input
                    value={bulkPromoLabel}
                    onChange={(e) => setBulkPromoLabel(e.target.value)}
                    placeholder='Es. "Last Minute -20%" oppure "—" per rimuovere'
                    data-testid="bulk-promo-label"
                    className="mt-1.5 h-10"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={bulkPromoType || "none"} onValueChange={(v) => setBulkPromoType(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="bulk-promo-type" className="mt-1.5 h-10">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="last_minute">Last minute</SelectItem>
                        <SelectItem value="seasonal">Stagionale</SelectItem>
                        <SelectItem value="special">Speciale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Sconto %</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="90"
                      value={bulkPromoPct}
                      onChange={(e) => setBulkPromoPct(e.target.value)}
                      placeholder="Es. 15"
                      data-testid="bulk-promo-pct"
                      className="mt-1.5 h-10"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-[#635F59]">
                  💡 Imposta anche il <strong>Prezzo personalizzato</strong> per rendere lo sconto effettivo.
                </p>
              </div>
            </BookingSection>
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 bg-white border-t border-[#E8E2D9] p-4 flex gap-2">
            <Button variant="outline" onClick={() => setDrawerOpen(false)} className="flex-1">Annulla</Button>
            <Button onClick={applyBulk} data-testid="apply-bulk" className="flex-1 bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              Applica modifiche
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      {/* Seasonal preset dialog */}
      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#E3A857]" />Preset stagionali
            </DialogTitle>
            <DialogDescription>
              Applica prezzi e min-stay per l'intero anno con un click. <strong>Camera:</strong> {selectedRoom?.name?.it}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Anno</Label>
              <Select value={presetYear} onValueChange={setPresetYear}>
                <SelectTrigger data-testid="preset-year" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1, 2].map((off) => {
                    const y = String(new Date().getFullYear() + off);
                    return <SelectItem key={y} value={y}>{y}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" />Alta · Lug-Ago</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={presetHigh}
                  onChange={(e) => setPresetHigh(e.target.value)}
                  placeholder="€"
                  data-testid="preset-high"
                  className="mt-1.5 h-10"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Media · Giu-Set + festività</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={presetMedium}
                  onChange={(e) => setPresetMedium(e.target.value)}
                  placeholder="€ (opz)"
                  data-testid="preset-medium"
                  className="mt-1.5 h-10"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Bassa · Resto anno</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={presetLow}
                  onChange={(e) => setPresetLow(e.target.value)}
                  placeholder={`Base €${selectedRoom?.price_per_night ?? "—"}`}
                  data-testid="preset-low"
                  className="mt-1.5 h-10"
                />
              </div>
            </div>
            <p className="text-xs text-[#635F59]">Lascia "Bassa" e "Media" vuote per usare il prezzo base della camera in quei mesi.</p>

            <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1E3A5F]">
                <Moon className="w-4 h-4" /> Regole weekend (Ven + Sab)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Supplemento weekend (%)</Label>
                  <Input
                    type="number" step="1" min="0"
                    value={presetWeekendPct}
                    onChange={(e) => setPresetWeekendPct(e.target.value)}
                    placeholder="0 = no"
                    data-testid="preset-weekend-pct"
                    className="mt-1.5 h-10"
                  />
                </div>
                <div>
                  <Label className="text-xs">Min notti weekend</Label>
                  <Input
                    type="number" step="1" min="0"
                    value={presetWeekendMinStay}
                    onChange={(e) => setPresetWeekendMinStay(e.target.value)}
                    placeholder="0 = no"
                    data-testid="preset-weekend-minstay"
                    className="mt-1.5 h-10"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setPresetOpen(false)}>Annulla</Button>
            <Button
              onClick={applyPreset}
              disabled={presetSubmitting || !presetHigh}
              data-testid="apply-preset"
              className="bg-[#1E3A5F] hover:bg-[#152A45] text-white"
            >
              {presetSubmitting ? "Applico..." : "Applica preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// === YearGrid — annual overview: 12 mini-calendars in a 3-row grid =========
// Each mini calendar shows the days of one month, colored by status
// (uniform with the monthly view). Clicking the header opens the monthly view.
function YearGrid({ yearAnchor, dayMap, today, onPickMonth, onToggleDayBlock, selectedDateSet, onStartDrag, onExtendDrag, onEndDrag }) {
  const months = useMemo(() => {
    const out = [];
    for (let i = 0; i < 12; i++) {
      out.push(addMonths(startOfYear(yearAnchor), i));
    }
    return out;
  }, [yearAnchor]);

  // End any drag if user releases the mouse outside the grid
  useEffect(() => {
    const handler = () => onEndDrag?.();
    window.addEventListener("mouseup", handler);
    window.addEventListener("touchend", handler);
    return () => {
      window.removeEventListener("mouseup", handler);
      window.removeEventListener("touchend", handler);
    };
  }, [onEndDrag]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 select-none" data-testid="year-grid">
      {months.map((m) => (
        <MiniMonth
          key={format(m, "yyyy-MM")}
          month={m}
          dayMap={dayMap}
          today={today}
          onPickMonth={onPickMonth}
          onToggleDayBlock={onToggleDayBlock}
          selectedDateSet={selectedDateSet}
          onStartDrag={onStartDrag}
          onExtendDrag={onExtendDrag}
          onEndDrag={onEndDrag}
        />
      ))}
    </div>
  );
}

function MiniMonth({ month, dayMap, today, onPickMonth, onToggleDayBlock, selectedDateSet, onStartDrag, onExtendDrag, onEndDrag }) {
  const monthDays = useMemo(() => eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }), [month]);
  const firstDow = (startOfMonth(month).getDay() + 6) % 7; // 0=Mon
  const padding = Array.from({ length: firstDow });
  const dragMovedRef = useRef(false);

  return (
    <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition" data-testid={`mini-month-${format(month, "yyyy-MM")}`}>
      <button
        type="button"
        onClick={() => onPickMonth(startOfMonth(month))}
        className="w-full px-3 py-2 bg-[#FDFBF7] border-b border-[#E8E2D9] flex items-center justify-between hover:bg-[#F5EFE6] transition group"
      >
        <span className="font-display text-sm font-semibold text-[#1E3A5F] capitalize">
          {format(month, "MMMM yyyy", { locale: itLocale })}
        </span>
        <ChevronRight className="w-4 h-4 text-[#AC5D45] group-hover:translate-x-0.5 transition" />
      </button>
      <div className="grid grid-cols-7 px-2 pt-2 text-[9px] uppercase tracking-wider text-[#635F59] font-semibold">
        {DAYS_SHORT.map((d) => <div key={d} className="text-center py-1">{d[0]}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5 p-2">
        {padding.map((_, i) => <div key={`p-${i}`} />)}
        {monthDays.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const info = dayMap[key];
          const isPast = isBefore(d, today) && !isSameDay(d, today);
          const blocked = info?.blocked === true;
          const availability = info?.availability;
          const isSel = selectedDateSet?.has(key);
          // OTA overlay (always derivable, even when host_override). Used to
          // render the small square lock badge above / below the cell.
          const isOtaBlock = availability === "ota_block";
          const otaOverlay = info?.ota_block_overlay || info?.booking?.ota_block_overlay || (isOtaBlock ? info?.source : "");
          const otaSrc = (otaOverlay || "").toLowerCase();
          // A real internal/external booking outbound-exports the date as
          // CLOSED to BOTH OTAs → show both locks in that case. A pure OTA
          // inbound block only closes the source OTA (we no longer export
          // OTA blocks back), so show only the matching lock.
          const isRealOccupant = availability === "booked" || availability === "external" || (blocked && !isOtaBlock);
          const isAirbnb = isRealOccupant || otaSrc.includes("airbnb");
          const isBookingOTA = isRealOccupant || otaSrc.includes("booking");
          // Yearly MiniMonth uses ONLY TWO visual states to stay clean:
          //  - LIBERO (green pastel)
          //  - CHIUSO (rose pastel + tiny A/B lock badges in the corners)
          // We deliberately don't paint "booked" cells in amber/orange here:
          // when a booking exists the date IS effectively closed, so the
          // rose+lock visual is the consistent signal.
          let tone = "bg-emerald-100 text-emerald-900 border-emerald-300";
          const isClosed = blocked || availability === "booked"
            || availability === "external" || availability === "ota_block";
          if (isClosed) {
            tone = "bg-rose-300 text-rose-900 border-rose-400";
          } else if (isPast) {
            // Past free days: keep them readable but neutral
            tone = "bg-gray-50 text-gray-500 border-gray-200";
          }
          if (isPast) tone += " opacity-75";
          if (isSel && !isPast) tone += " ring-2 ring-[#1E3A5F] ring-offset-1";
          return (
            <div key={key} className="relative">
              <button
                type="button"
                data-day={key}
                onMouseDown={(e) => {
                  e.preventDefault();
                  dragMovedRef.current = false;
                  onStartDrag?.(d);
                }}
                onMouseEnter={() => {
                  dragMovedRef.current = true;
                  onExtendDrag?.(d);
                }}
                onMouseUp={() => onEndDrag?.()}
                onTouchStart={(e) => {
                  e.preventDefault();
                  dragMovedRef.current = false;
                  onStartDrag?.(d);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (dragMovedRef.current) return;
                  onToggleDayBlock?.(d, !!blocked);
                }}
                className={`aspect-square w-full rounded text-[10px] font-semibold border ${tone} flex items-center justify-center hover:scale-110 transition cursor-pointer`}
                title={`${key}${blocked ? " · CHIUSO" : availability === "booked" ? " · Prenotato" : availability === "external" ? " · Esterno" : isOtaBlock ? ` · Bloccato su ${otaOverlay || "OTA"}` : " · Libero"}${otaOverlay && !isOtaBlock ? ` · 🔒 Anche chiuso su ${otaOverlay}` : ""} — Doppio click=toggle · Trascina=selezione multipla`}
              >
                {format(d, "d")}
              </button>
              {/* OTA lock badges in the cell corners — DO NOT cover the
                  pastel background. Airbnb top-left, Booking top-right. */}
              {isAirbnb && (
                <span
                  className="absolute -top-1 -left-1 inline-flex items-center justify-center text-[6px] font-extrabold rounded-sm border bg-pink-100 border-pink-500 text-pink-700 leading-none shadow-sm pointer-events-none z-10 w-3 h-3"
                  data-testid={`year-ota-lock-airbnb-${key}`}
                  title={`🔒 Chiuso su ${otaOverlay}`}
                >
                  A
                </span>
              )}
              {isBookingOTA && (
                <span
                  className="absolute -top-1 -right-1 inline-flex items-center justify-center text-[6px] font-extrabold rounded-sm border bg-sky-100 border-sky-500 text-sky-700 leading-none shadow-sm pointer-events-none z-10 w-3 h-3"
                  data-testid={`year-ota-lock-booking-${key}`}
                  title={`🔒 Chiuso su ${otaOverlay}`}
                >
                  B
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

